import { useEffect, useRef, useState } from 'react';
import {
  Badge, Button, Center, Group, Loader, Modal, Paper, SegmentedControl, Stack, Text, Textarea,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconRefresh, IconUserX, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { SectionCard } from './SectionCard';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../lib/format';
import { SILENT_ERROR } from '../lib/queryMeta';

type Holat = 'NEW' | 'DONE' | 'REJECTED';
interface Sorov {
  id: string;
  login: string;
  fullName: string;
  role: string;
  reason: string | null;
  source: 'APP' | 'WEB';
  status: Holat;
  createdAt: string;
  processedAt: string | null;
  processedByName: string | null;
  rejectReason: string | null;
}

/** Sahifa ichidagi havola — bildirishnoma (`screen: 'AccountDeletion'`) shu yerga olib keladi. */
export const ACCOUNT_DELETION_ANCHOR = 'akkaunt-ochirish';

const HOLAT_RANGI: Record<Holat, string> = { NEW: 'orange', DONE: 'gray', REJECTED: 'blue' };
const SABAB_MAX = 500;
const xatoMatni = (e: unknown, zaxira: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? zaxira;

/**
 * AKKAUNTNI O'CHIRISH SO'ROVLARI — maktab rahbariyati (direktor, admin) uchun, Sozlamalar.
 * Backend: `GET /account-deletion`, `POST /:id/done` (anonimlashtirish), `POST /:id/reject`.
 * Akkaunt so'rov paytidayoq yopilgan; bu yerda faqat yakunlanadi.
 */
export function AccountDeletionRequests() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  const location = useLocation();
  const kartaRef = useRef<HTMLDivElement>(null);
  const [filtr, setFiltr] = useState<'NEW' | 'ALL'>('NEW');
  const [radEt, setRadEt] = useState<Sorov | null>(null);
  const [radSabab, setRadSabab] = useState('');

  const kalit = ['account-deletion', user?.tenantId ?? null, filtr] as const;
  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: kalit,
    queryFn: async () =>
      (await api.get<{ items: Sorov[]; yangi: number }>('/account-deletion', { params: filtr === 'NEW' ? { status: 'NEW' } : {} })).data,
    meta: SILENT_ERROR,
  });

  /* Bildirishnomadan kelinganda (`/settings#akkaunt-ochirish`) kartaga aylantiramiz */
  useEffect(() => {
    if (location.hash === `#${ACCOUNT_DELETION_ANCHOR}` && !isLoading) {
      kartaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash, isLoading]);

  const yangila = () => qc.invalidateQueries({ queryKey: ['account-deletion'] });

  const bajar = useMutation({
    mutationFn: async (id: string) => (await api.post(`/account-deletion/${id}/done`)).data,
    onSuccess: () => { notifications.show({ color: 'green', message: t('deletionRequests.doneOkMsg') }); void yangila(); },
    onError: (e) => { notifications.show({ color: 'red', message: xatoMatni(e, t('common.error')) }); void yangila(); },
  });
  const rad = useMutation({
    mutationFn: async (v: { id: string; reason: string }) =>
      (await api.post(`/account-deletion/${v.id}/reject`, { reason: v.reason.trim() || undefined })).data,
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('deletionRequests.rejectOkMsg') });
      setRadEt(null);
      setRadSabab('');
      void yangila();
    },
    onError: (e) => { notifications.show({ color: 'red', message: xatoMatni(e, t('common.error')) }); void yangila(); },
  });

  const bajarishniTasdiqla = (s: Sorov) =>
    modals.openConfirmModal({
      title: t('deletionRequests.confirmDoneTitle'),
      centered: true,
      children: <Text size="sm">{t('deletionRequests.confirmDoneText', { name: s.fullName })}</Text>,
      labels: { confirm: t('deletionRequests.confirmDoneOk'), cancel: t('deletionRequests.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => bajar.mutate(s.id),
    });

  const yangi = data?.yangi ?? 0;

  return (
    <div ref={kartaRef} id={ACCOUNT_DELETION_ANCHOR} style={{ scrollMarginTop: 80 }}>
      <SectionCard
        title={t('deletionRequests.title')}
        subtitle={t('deletionRequests.subtitle')}
        icon={IconUserX}
        color={yangi > 0 ? 'orange' : 'brand'}
        maw={520}
        mb="md"
      >
        <Stack gap="sm">
          <SegmentedControl
            size="xs"
            value={filtr}
            onChange={(v) => setFiltr(v as 'NEW' | 'ALL')}
            data={[
              { value: 'NEW', label: t('deletionRequests.filterNew', { n: yangi }) },
              { value: 'ALL', label: t('deletionRequests.filterAll') },
            ]}
          />
          {isLoading ? (
            <Center py="md"><Loader size="sm" /></Center>
          ) : isError ? (
            <Stack gap="sm" align="flex-start">
              <Text size="sm" c="red">{t('deletionRequests.loadError')}</Text>
              <Button size="xs" variant="light" color="red" leftSection={<IconRefresh size={14} />}
                loading={isFetching} onClick={() => void refetch()}>
                {t('deletionRequests.retry')}
              </Button>
            </Stack>
          ) : !data?.items.length ? (
            <Text size="sm" c="dimmed">{t('deletionRequests.empty')}</Text>
          ) : (
            data.items.map((s) => (
              <Paper key={s.id} withBorder radius="md" p="sm">
                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Text fw={600} size="sm" truncate>{s.fullName}</Text>
                    <Text size="xs" c="dimmed" ff="monospace" truncate>{s.login}</Text>
                  </Stack>
                  <Group gap={4} wrap="nowrap">
                    <Badge size="sm" variant="light">{t(`roles.${s.role}`)}</Badge>
                    <Badge size="sm" variant="light" color={HOLAT_RANGI[s.status]}>{t(`deletionRequests.status.${s.status}`)}</Badge>
                  </Group>
                </Group>
                <Text size="xs" c="dimmed" mt={6}>
                  {t('deletionRequests.requested', { date: formatDateTime(s.createdAt) })} · {t(`deletionRequests.source.${s.source}`)}
                </Text>
                {s.reason && (
                  <Text size="sm" mt={4} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {t('deletionRequests.reason', { text: s.reason })}
                  </Text>
                )}
                {s.status === 'NEW' ? (
                  <Group gap="xs" mt="sm">
                    <Button size="xs" color="red" leftSection={<IconCheck size={14} />}
                      loading={bajar.isPending && bajar.variables === s.id} onClick={() => bajarishniTasdiqla(s)}>
                      {t('deletionRequests.done')}
                    </Button>
                    <Button size="xs" variant="default" leftSection={<IconX size={14} />}
                      onClick={() => { setRadSabab(''); setRadEt(s); }}>
                      {t('deletionRequests.reject')}
                    </Button>
                  </Group>
                ) : (
                  <>
                    <Text size="xs" c="dimmed" mt={4}>
                      {t('deletionRequests.processed', {
                        date: s.processedAt ? formatDateTime(s.processedAt) : '—',
                        by: s.processedByName ? ` · ${s.processedByName}` : '',
                      })}
                    </Text>
                    {s.rejectReason && (
                      <Text size="xs" mt={2} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {t('deletionRequests.rejectReasonShown', { text: s.rejectReason })}
                      </Text>
                    )}
                  </>
                )}
              </Paper>
            ))
          )}
        </Stack>
      </SectionCard>

      <Modal opened={!!radEt} onClose={() => setRadEt(null)} title={t('deletionRequests.rejectTitle')} centered>
        <Stack gap="sm">
          <Text size="sm">{t('deletionRequests.rejectText', { name: radEt?.fullName ?? '' })}</Text>
          <Textarea
            label={t('deletionRequests.rejectReason')}
            autosize
            minRows={2}
            maxRows={5}
            maxLength={SABAB_MAX}
            value={radSabab}
            onChange={(e) => setRadSabab(e.currentTarget.value)}
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setRadEt(null)}>{t('deletionRequests.cancel')}</Button>
            <Button loading={rad.isPending} onClick={() => radEt && rad.mutate({ id: radEt.id, reason: radSabab })}>
              {t('deletionRequests.rejectOk')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
